/*
 * Defense in depth for redemption-only collectibles.
 *
 * Shopify's public cart endpoint can be called without using the product form,
 * so hiding a buy button or constraining its quantity is not sufficient. This
 * guard quarantines collectible variants injected through /cart/add and blocks
 * checkout while cleanup is in progress. Authoritative enforcement must also
 * be configured with Shopify Cart and Checkout Validation (see SECURITY.md).
 */
(function () {
  var cleanupStarted = false;

  function normalized(value) {
    return String(value || '').trim().toLowerCase();
  }

  function isRedemptionOnly(item) {
    var title = normalized(item.product_title || item.title);
    var type = normalized(item.product_type);
    var handle = normalized(item.handle);
    var tags = Array.isArray(item.tags) ? item.tags.join(',').toLowerCase() : normalized(item.tags);

    if (hasValidClaimShape(item) && (type === 'collectible' || title.indexOf('collectible') !== -1 ||
      handle.indexOf('collectible') !== -1 || tags.indexOf('nft collectible') !== -1 ||
      tags.indexOf('redemption-only') !== -1)) return false;

    return type === 'collectible' || title.indexOf('collectible') !== -1 ||
      handle.indexOf('collectible') !== -1 || tags.indexOf('nft collectible') !== -1 ||
      tags.indexOf('redemption-only') !== -1;
  }

  function hasValidClaimShape(item) {
    var properties = item && item.properties ? item.properties : {};
    var wallet = normalized(properties._better_human_wallet_address);
    var claim = String(properties._better_human_purchase_claim || '').trim();
    return Number(item.quantity) === 1 && wallet.length > 0 && claim.length > 0;
  }

  function violationDetails(items) {
    return items.map(function (item) {
      var properties = item && item.properties ? item.properties : {};
      var issues = [];
      if (Number(item.quantity) !== 1) issues.push('quantity_must_equal_one');
      if (!normalized(properties._better_human_wallet_address)) issues.push('wallet_missing');
      if (!String(properties._better_human_purchase_claim || '').trim()) issues.push('purchase_claim_missing');
      return {
        product: item.product_title || item.title || 'collectible',
        variantId: item.variant_id || item.id || '',
        quantity: Number(item.quantity || 0),
        issues: issues.length ? issues : ['claim_requires_server_validation']
      };
    });
  }

  function reportViolation(items, resolution) {
    var bot = window.analyticsSecurityBot;
    if (!bot || typeof bot.reportCollectibleViolation !== 'function') return;
    bot.reportCollectibleViolation(violationDetails(items), resolution);
  }

  function lockCheckout() {
    document.querySelectorAll(
      'button[name="checkout"], input[name="checkout"], .additional-checkout-buttons, shopify-accelerated-checkout-cart'
    ).forEach(function (element) {
      element.setAttribute('aria-disabled', 'true');
      element.setAttribute('inert', '');
      if ('disabled' in element) element.disabled = true;
    });
  }

  function removeLines(items) {
    if (cleanupStarted || !items.length) return Promise.resolve(false);
    cleanupStarted = true;
    lockCheckout();

    var updates = {};
    items.forEach(function (item) {
      updates[item.key] = 0;
    });

    reportViolation(items, {
      status: 'needs_review',
      action: 'checkout_locked',
      detail: 'Invalid collectible lines were detected and checkout was locked while removal began.'
    });

    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ updates: updates })
    }).then(function (response) {
      if (!response.ok) throw new Error('Unable to remove redemption-only cart items');
      reportViolation(items, {
        status: 'resolved',
        action: 'unauthorized_collectibles_removed',
        detail: 'Invalid collectible lines were removed from the Shopify cart before checkout.'
      });
      window.location.reload();
      return true;
    }).catch(function (error) {
      cleanupStarted = false;
      lockCheckout();
      reportViolation(items, {
        status: 'failed',
        action: 'checkout_remains_locked',
        detail: 'Automatic cart cleanup failed; checkout remains locked for manual review.'
      });
      console.error(error);
      return false;
    });
  }

  function inspectCart() {
    return fetch('/cart.js', {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    }).then(function (response) {
      if (!response.ok) throw new Error('Unable to inspect cart');
      return response.json();
    }).then(function (cart) {
      var invalidItems = (cart.items || []).filter(isRedemptionOnly);
      if (invalidItems.length) return removeLines(invalidItems);
      return false;
    }).catch(function (error) {
      console.error(error);
      return false;
    });
  }

  document.addEventListener('submit', function (event) {
    var submitter = event.submitter;
    if (cleanupStarted && submitter && submitter.name === 'checkout') {
      event.preventDefault();
      lockCheckout();
    }
  }, true);

  document.addEventListener('cart:update', inspectCart);
  document.addEventListener('DOMContentLoaded', inspectCart, { once: true });
  window.addEventListener('pageshow', inspectCart);
})();
